import { useState } from 'react';
import PercentIcon from '@mui/icons-material/Percent';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
	Button, Datepicker, Dialog, NumberField, Text, WarnChip
} from '@components';
import { BUTTON_TYPES, PartialInputEvent, TCPIIncrease, TUpdateCPIIncreaseRequest } from '@types';
import { formatDate } from '@utils';
import { useValidation } from '@hooks';
import { z } from 'zod';
import isSameDay from 'date-fns/isSameDay';
import styles from './CPIIncreaseScheduleDialog.module.scss';

const getSydneyToday = (): Date => {
	const sydneyStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
	const [year, month, day] = sydneyStr.split('-').map(Number);
	return new Date(year, month - 1, day, 0, 0, 0, 0);
};

type TFormState = {
	cpi_percentage: string;
	effective_date: Date|'';
}

const properties = {
	'bulk-create': { title: 'Schedule CPI Increase', text: '' },
	create: {
		title: 'Schedule CPI Increase',
		text: 'You are about to apply a CPI percentage increase to the base rates for this specific rate card. This update will affect this specific lane-level values (excluding modifiers).',
	},
	edit: {
		title: 'Edit Scheduled CPI Increase',
		text: 'You are editing the upcoming CPI percentage increase to the base rates for this specific rate card. This update will affect this specific lane-level values (excluding modifiers).',
	},
};

type TProps = {
	type: 'bulk-create' | 'create' | 'edit';
	cpiIncrease?: TCPIIncrease;
	isOpen: boolean;
	onClose: () => void;
	isLoading: boolean;
	onSubmit: (request: TUpdateCPIIncreaseRequest) => void;
	text?: string;
	onDeleteClick?: () => void;
	someHaveScheduledIncrease?: boolean;
}

export const CPIIncreaseScheduleDialog = ({ type, isOpen, onClose, isLoading, onSubmit, cpiIncrease, text, onDeleteClick, someHaveScheduledIncrease = false }: TProps) => {
	const [form, setForm] = useState<TFormState>({
		cpi_percentage: cpiIncrease?.cpi_percentage || '',
		effective_date: cpiIncrease?.scheduled_at ? new Date(cpiIncrease?.scheduled_at) : '',
	});
	const {
		connect,
		formValid,
	} = useValidation({
		cpi_percentage: z.string()
			.min(1, { message: 'The percentage is required' })
			.regex(/^-?\d*\.?\d+$/, 'Must be a valid number')
			.refine((val) => {
				const num = parseFloat(val);
				return num > -100;
			}, { message: 'Value must be greater than -100' }),
		effective_date: z.date(),
	});

	const handleChange = (e: PartialInputEvent) => {
		const { name, value } = e.target;
		setForm((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async () => {
		if (!formValid(form).success) return;
		const scheduledAt = formatDate(form.effective_date as Date, 'API_DATE_FORMAT');
		await onSubmit({ scheduled_at: scheduledAt, cpi_percentage: form.cpi_percentage });
	};

	const sydneyToday = getSydneyToday();
	const isToday = form.effective_date instanceof Date && isSameDay(form.effective_date, sydneyToday);

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			title={properties[type].title}
			text={text ?? properties[type].text}
			actionButtonText={'Schedule Increase'}
			actionButtonFn={handleSubmit}
			actionButtonLoading={isLoading}
			closeOnButtonAction={false}
			closeOnClickOutside={false}
			closeOnEscape={true}
			slot={
				<>
					{someHaveScheduledIncrease && (
						<WarnChip text='Rate cards have a scheduled CPI increase. Proceeding will overwrite these scheduled increases with this new update.' />
					)}
					<div className={styles.form}>
						<NumberField
							label='CPI Increase (%)'
							name='cpi_percentage'
							value={form.cpi_percentage}
							Icon={PercentIcon}
							step={'0.01'}
							min={'0'}
							onChange={handleChange}
							placeholder={'0.00'}
							autoFocus
							{...connect('cpi_percentage')}
						/>
						<Datepicker
							label='Effective Date'
							name='effective_date'
							value={form.effective_date}
							onChange={(e) => handleChange(e as PartialInputEvent)}
							dateOnly
							minDate={sydneyToday}
							keepErrorSpace={false}
							{...connect('effective_date')}
						/>
						{isToday && (<Text>Today selected. This increase will be processed immediately.</Text>)}
					</div>
					{type === 'bulk-create' && onDeleteClick && someHaveScheduledIncrease && (
						<div>
							<Button
								text='Delete CPI increases'
								type={BUTTON_TYPES.TERTIARY}
								Icon={DeleteOutlineIcon}
								onClick={onDeleteClick}
							/>
						</div>
					)}
				</>
			}
		/>
	);
};
